import { useState } from "react";
import { Phone, MessageCircle, MapPin, Mail, Instagram, ArrowUpRight } from "lucide-react";
import { COMPANY, buildWhatsAppUrl } from "../../lib/constants";
import { LegalModal } from "../LegalModal";
import { GoogleBadge } from "../GoogleBadge";

export const Footer = () => {
  const [modal, setModal] = useState(null);

  return (
    <footer
      data-testid="footer-section"
      className="relative pt-20 pb-10 bg-black text-white overflow-hidden border-t border-white/5"
    >
      <div className="bg-noise absolute inset-0 opacity-25 pointer-events-none" />
      <div className="relative px-4 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 pb-12 border-b border-white/10">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white p-2 flex items-center justify-center">
                <img src={COMPANY.logo} alt="BRS" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-medium tracking-tight font-display">
                  Bremer Recycling
                </div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45 mt-1.5">
                  Service · Premium Entrümpelung
                </div>
              </div>
            </div>
            <p className="mt-6 max-w-md text-white/60 text-sm leading-relaxed">
              Haushaltsauflösung, Entrümpelung, Entsorgung und kostenlose
              Schrottabholung in Bremen und Umgebung. Ein Anruf, ein Termin,
              alles sauber.
            </p>
            <div className="mt-6">
              <GoogleBadge variant="dark" />
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45">· Kontakt</div>
            <ul className="mt-4 space-y-3">
              <li>
                <a href={`tel:${COMPANY.phoneRaw}`} data-testid="footer-phone-link" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                  <span className="text-base font-semibold">{COMPANY.phone}</span>
                </a>
              </li>
              <li>
                <a href={buildWhatsAppUrl()} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link" className="flex items-center gap-3 text-white/80 hover:text-[#25D366] transition-colors">
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-base font-semibold">WhatsApp</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${COMPANY.email}?subject=Anfrage%20Bremer%20Recycling%20Service`} data-testid="footer-mail-link" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                  <Mail className="w-4 h-4 shrink-0" />
                  <span className="text-sm md:text-[15px] font-semibold break-all">{COMPANY.email}</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/60">
                <MapPin className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">{COMPANY.area}</span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45">· Folgen</div>

            {/* Instagram card */}
            <a
              href={COMPANY.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-instagram"
              className="group mt-4 flex items-center gap-3 p-4 rounded-2xl border border-white/15 hover:border-transparent bg-white/[0.03] hover:bg-gradient-to-tr hover:from-[#feda75] hover:via-[#d62976] hover:to-[#4f5bd5] transition-all"
            >
              <span className="w-11 h-11 rounded-xl bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] flex items-center justify-center shrink-0">
                <Instagram className="w-5 h-5 text-white" strokeWidth={2.5} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/55 group-hover:text-white/85">
                  Instagram
                </div>
                <div className="text-sm font-semibold text-white truncate">
                  {COMPANY.instagramHandle}
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-white/65 group-hover:text-white group-hover:rotate-45 transition-all shrink-0" />
            </a>

            <div className="mt-5">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/45 mb-3">· Navigation</div>
              <ul className="space-y-2 text-sm">
                {[
                  ["#leistungen", "Leistungen"],
                  ["#bewertungen", "Bewertungen"],
                  ["#angebot", "Angebot"],
                  ["#faq", "FAQ"],
                ].map(([h, l]) => (
                  <li key={h}>
                    <a href={h} className="text-white/65 hover:text-white transition-colors font-medium">{l}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Massive wordmark */}
        <div className="py-8 md:py-10 select-none">
          <div className="font-display font-semibold tracking-tighter leading-[0.85] text-[18vw] lg:text-[14vw] text-white/95">
            BRS · BREMEN
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-xs text-white/45 border-t border-white/10 pt-6">
          <div>© {new Date().getFullYear()} Bremer Recycling Service. Alle Rechte vorbehalten.</div>
          <div className="flex gap-5">
            <button
              onClick={() => setModal("impressum")}
              data-testid="footer-impressum-btn"
              className="hover:text-white transition-colors"
            >
              Impressum
            </button>
            <button
              onClick={() => setModal("datenschutz")}
              data-testid="footer-datenschutz-btn"
              className="hover:text-white transition-colors"
            >
              Datenschutz
            </button>
          </div>
        </div>
      </div>

      <LegalModal open={modal === "impressum"} onClose={() => setModal(null)} type="impressum" />
      <LegalModal open={modal === "datenschutz"} onClose={() => setModal(null)} type="datenschutz" />
    </footer>
  );
};
